// src/implementations/AbstractEventDispatcher.ts
var AbstractEventDispatcher = class {
  // Store bound listeners for subscribers to enable proper removal
  subscriberListeners;
  constructor() {
    this.subscriberListeners = /* @__PURE__ */ new WeakMap();
  }
  isStoppableEvent(event) {
    return "isPropagationStopped" in event && typeof event.isPropagationStopped === "function";
  }
  addSubscriber(subscriber) {
    const events = subscriber.getSubscribedEvents();
    const boundListeners = /* @__PURE__ */ new Map();
    let listenerName;
    let priority;
    for (const [eventName, params] of Object.entries(events)) {
      priority = 0;
      if (typeof params === "string") {
        listenerName = params;
      } else {
        listenerName = params.listener;
        priority = params.priority ?? 0;
      }
      const boundListener = subscriber[listenerName].bind(subscriber);
      boundListeners.set(eventName, boundListener);
      this.addListener(eventName, boundListener, priority);
    }
    this.subscriberListeners.set(subscriber, boundListeners);
  }
  removeSubscriber(subscriber) {
    const boundListeners = this.subscriberListeners.get(subscriber);
    if (!boundListeners) {
      return;
    }
    for (const [eventName, boundListener] of boundListeners) {
      this.removeListener(eventName, boundListener);
    }
    this.subscriberListeners.delete(subscriber);
  }
  /**
   * Dispatches an event synchronously to all registered listeners,
   * in priority order (highest first).
   *
   * Async listeners are fire-and-forget: their Promise errors are caught
   * and logged, but dispatch() does NOT await them.
   * Use dispatchAsync() when you need to await async listeners.
   *
   * This mirrors Symfony's EventDispatcher::dispatch() behaviour.
   */
  dispatch(event, eventName) {
    const name = eventName ?? event.constructor.name;
    if (!this.hasListeners(name)) {
      return event;
    }
    const sortedListeners = this.getListeners(name);
    const isStoppable = this.isStoppableEvent(event);
    for (const listener of sortedListeners) {
      if (isStoppable && event.isPropagationStopped()) {
        break;
      }
      try {
        const result = listener(event);
        if (result instanceof Promise) {
          result.catch((error) => {
            console.error(
              `Unhandled async error in listener for "${name}":`,
              error
            );
          });
        }
      } catch (error) {
        console.error(`Error in listener for "${name}":`, error);
      }
    }
    return event;
  }
  /**
   * Dispatches an event and awaits each listener sequentially,
   * in priority order.
   *
   * Use this when subscribers perform async operations (HTTP, file I/O, DB…)
   * and you need to read results from the event object after dispatch.
   *
   * stopPropagation() is honoured between each awaited listener.
   *
   * @example
   * const event = new InitializingUploadEvent(options);
   * await dispatcher.dispatchAsync(event, HttpFileUploaderEvents.INITIALIZE_UPLOAD);
   * const mediaId = event.mediaId; // safely populated by the subscriber
   */
  async dispatchAsync(event, eventName) {
    const name = eventName ?? event.constructor.name;
    if (!this.hasListeners(name)) {
      return event;
    }
    const sortedListeners = this.getListeners(name);
    const isStoppable = this.isStoppableEvent(event);
    for (const listener of sortedListeners) {
      if (isStoppable && event.isPropagationStopped()) {
        break;
      }
      try {
        const result = listener(event);
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        throw error;
      }
    }
    return event;
  }
};

// src/implementations/BrowserEventDispatcher.ts
var BrowserEventDispatcher = class extends AbstractEventDispatcher {
  constructor(eventTarget = new EventTarget(), options) {
    super();
    this.eventTarget = eventTarget;
    this.options = options;
    this.sorted = /* @__PURE__ */ new Map();
    this.listenerMap = /* @__PURE__ */ new WeakMap();
    this.listeners = /* @__PURE__ */ new Map();
  }
  listeners;
  sorted;
  listenerMap;
  /**
   * Dispatches an event synchronously.
   *
   * Step 1 — Internal Map loop (priority order):
   *   Calls all listeners registered via addListener() / addSubscriber().
   *   stopPropagation() is honoured between each listener.
   *   Async listeners are fire-and-forget (errors logged, not thrown).
   *   Use dispatchAsync() if you need to await async listeners.
   *
   * Step 2 — Native EventTarget (dispatchNative):
   *   Fires a CustomEvent so that any external listener attached via
   *   window.addEventListener() / document.addEventListener() on the same
   *   target also receives the event — without ever registering through
   *   this dispatcher.
   *
   * Note: stopPropagation() on YOUR event object does NOT prevent Step 2.
   * The CustomEvent dispatched natively is independent and can be cancelled
   * via nativeEvent.stopPropagation() on the browser side.
   */
  dispatch(event, eventName, _customOptions) {
    super.dispatch(event, eventName);
    this.dispatchNative(event, eventName, _customOptions);
    return event;
  }
  /**
   * Dispatches an event and awaits all listeners sequentially (priority order).
   *
   * Step 1 — awaits each async listener in the internal Map.
   * Step 2 — fires the native CustomEvent (fire-and-forget, not awaited —
   *           native EventTarget listeners are synchronous by design).
   *
   * Use this when subscribers perform async work (HTTP, file I/O…) and
   * you need to read results from the event object after dispatch.
   *
   * @example
   * const event = new InitializingUploadEvent(options);
   * await dispatcher.dispatchAsync(event, HttpFileUploaderEvents.INITIALIZE_UPLOAD);
   * const mediaId = event.mediaId; // safely populated by the subscriber
   */
  async dispatchAsync(event, eventName, _customOptions) {
    await super.dispatchAsync(event, eventName);
    this.dispatchNative(event, eventName, _customOptions);
    return event;
  }
  /**
   * Registers a listener in the internal Map only.
   *
   * NOT attached to the native EventTarget — that target is reserved for
   * external listeners (window.addEventListener, etc.) to avoid double-call.
   */
  addListener(eventName, listener, priority = 0) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    const wrappedListener = ((nativeEvent) => {
      const customEvent = nativeEvent;
      listener(customEvent.detail);
    });
    this.listeners.get(eventName).push({
      listener,
      priority,
      wrappedListener
    });
    this.sorted.set(eventName, false);
    this.listenerMap.set(listener, wrappedListener);
  }
  /**
   * Removes a listener from the internal Map only.
   * Has no effect on listeners attached directly via addEventListener().
   */
  removeListener(eventName, listener) {
    if (!this.listeners.has(eventName)) {
      return;
    }
    const eventListeners = this.listeners.get(eventName);
    const index = eventListeners.findIndex((item) => item.listener === listener);
    if (index !== -1) {
      const item = eventListeners[index];
      if (item) {
        const { wrappedListener } = item;
        this.eventTarget.removeEventListener(eventName, wrappedListener);
        eventListeners.splice(index, 1);
        this.listenerMap.delete(listener);
      }
    }
    if (eventListeners.length === 0) {
      this.listeners.delete(eventName);
      this.sorted.delete(eventName);
    }
  }
  getListeners(eventName) {
    if (eventName) {
      if (!this.listeners.has(eventName)) {
        return [];
      }
      if (!this.sorted.get(eventName)) {
        this.sortListeners(eventName);
      }
      return this.listeners.get(eventName).map((item) => item.listener);
    }
    const allListeners = /* @__PURE__ */ new Map();
    for (const [name] of this.listeners) {
      allListeners.set(name, this.getListeners(name));
    }
    return allListeners;
  }
  getListenerPriority(eventName, listener) {
    if (!this.listeners.has(eventName)) {
      return null;
    }
    const found = this.listeners.get(eventName).find((item) => item.listener === listener);
    return found ? found.priority : null;
  }
  hasListeners(eventName) {
    if (eventName) {
      return this.listeners.has(eventName) && this.listeners.get(eventName).length > 0;
    }
    return this.listeners.size > 0;
  }
  /**
   * Returns the underlying EventTarget.
   *
   * Pass window or document in the constructor to share the same target
   * with the rest of your application — any addEventListener() call on
   * that target will automatically receive events dispatched via dispatch().
   *
   * @example
   * const dispatcher = new BrowserEventDispatcher(window);
   *
   * // Somewhere else in your app — no addListener() needed
   * window.addEventListener('user.login', (e) => {
   *   console.log((e as CustomEvent).detail);
   * });
   *
   * // This notifies both your subscribers AND the window listener
   * dispatcher.dispatch(new UserLoginEvent('franck'), 'user.login');
   */
  getEventTarget() {
    return this.eventTarget;
  }
  sortListeners(eventName) {
    const listeners = this.listeners.get(eventName);
    listeners.sort((a, b) => b.priority - a.priority);
    this.sorted.set(eventName, true);
  }
  /**
   * Fires a CustomEvent on the native EventTarget.
   *
   * Called automatically by dispatch() and dispatchAsync().
   * Can also be called manually if you want to notify native listeners
   * without going through the internal Map.
   *
   * External listeners receive the original event object via
   * `(e as CustomEvent).detail`.
   *
   * @example
   * // External code — no addListener() needed, works automatically
   * window.addEventListener('user.login', (e) => {
   *   const event = (e as CustomEvent).detail;
   *   console.log(event.username);
   * });
   *
   * dispatcher.dispatch(new UserLoginEvent('franck'), 'user.login');
   * // ↑ window listener receives the event automatically via dispatchNative
   */
  dispatchNative(event, eventName, _customOptions) {
    const name = eventName ?? event.constructor.name;
    const customEvent = new CustomEvent(name, {
      detail: event,
      bubbles: _customOptions?.bubbles || this.options?.bubbles || true,
      cancelable: _customOptions?.cancelable || this.options?.cancelable || true,
      composed: _customOptions?.composed || this.options?.composed || true
    });
    this.eventTarget.dispatchEvent(customEvent);
  }
};
export {
  BrowserEventDispatcher
};
